import { ArrowLeft } from 'lucide-react';
import { useState, type MouseEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from './cn';
import { ConfirmDialog } from './overlays';
import { Switch } from './fields';
import { Mark } from './shell';
import { Card, CardHeader, LaneProgress } from './surfaces';

/** The frames an app's pages are built in. Words come in as props: the package holds none. */

/** A page's frame on the canvas: the header, then cards — across the whole width the shell leaves it (operator, 2026-10-22). */
export function Page({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('grid w-full content-start gap-5 px-4 py-5 sm:px-6 sm:py-7', className)}>{children}</div>;
}

/** The filters above a table. */
export function Toolbar({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('flex flex-wrap items-end gap-3 border-b border-line p-4', className)}>{children}</div>;
}

/** "← All trips": a back link with an arrow that points the reading way back. A form page's comes from useWayBack (`{...back.link}`). */
export function BackLink({ to, onClick, children }: { to: string; onClick?: (event: MouseEvent<HTMLAnchorElement>) => void; children: ReactNode }) {
  return (
    <Link to={to} onClick={onClick} className="inline-flex w-fit items-center gap-1.5 text-body-sm font-bold no-underline">
      <ArrowLeft size={16} aria-hidden="true" className="rtl:-scale-x-100" />
      {children}
    </Link>
  );
}

/**
 * A part of a form page — every form is a page, never a popup (operator, 2026-10-24): a card, its
 * title, the fields in a grid of one column on a phone, two from `sm` and four on the widest screens,
 * so pairs (English beside Arabic, from beside to) stay side by side. A field takes the whole row
 * with `col-span-full`, two columns with `sm:col-span-2`.
 */
export function FormSection({ title, description, children, className }: { title?: ReactNode; description?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <Card>
      {title && <CardHeader title={title} description={description} />}
      <div className={cn('grid gap-4 p-4 sm:grid-cols-2 sm:p-5 2xl:grid-cols-4', className)}>{children}</div>
    </Card>
  );
}

/** A form page's actions, the primary one last, in a bar at its foot that stays in reach while a long form scrolls. */
export function FormActions({ children }: { children: ReactNode }) {
  return <div className="sticky bottom-0 z-panel -mx-4 flex flex-wrap items-center justify-end gap-2 border-t border-line bg-canvas px-4 py-3 sm:-mx-6 sm:px-6">{children}</div>;
}

/** A label and a value, stacked (record details) — put several in a `<dl>`. */
export function Detail({ label, children, className }: { label: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className={cn('grid min-w-0 content-start gap-1', className)}>
      <dt className="text-sign uppercase text-ink-subtle">{label}</dt>
      <dd className="m-0 min-w-0 text-body text-ink">{children}</dd>
    </div>
  );
}

/** A whole-screen wait: the lane flowing under the mark. */
export function ScreenLoader({ label }: { label: string }) {
  return (
    <div className="grid h-dvh place-items-center bg-canvas">
      <div className="grid w-[220px] justify-items-center gap-5">
        <Mark size={48} />
        <LaneProgress label={label} className="w-full" />
      </div>
    </div>
  );
}

/** The page area's wait while a page's code loads. */
export function PageLoader({ label }: { label: string }) {
  return (
    <div className="px-6 pt-6">
      <LaneProgress label={label} className="mx-auto w-40" />
    </div>
  );
}

export interface StatusToggleProps {
  active: boolean;
  /** Read with the switch: "Truck 14". */
  name: string;
  copy: {
    active: string;
    inactive: string;
    activate: string;
    deactivate: string;
    activateTitle: string;
    activateBody: string;
    deactivateTitle: string;
    deactivateBody: string;
    cancel: string;
    close: string;
  };
  /** Resolves when the API has it; rejects (after the caller's toast) to keep the dialog open. */
  onConfirm: (next: 'ACTIVE' | 'INACTIVE') => Promise<unknown>;
  disabled?: boolean;
}

/** Active / inactive as a switch that asks first; the switch moves only once the API agrees. */
export function StatusToggle({ active, name, copy, onConfirm, disabled }: StatusToggleProps) {
  const [asking, setAsking] = useState(false);
  return (
    <>
      <label className="inline-flex cursor-pointer items-center gap-2.5 text-body-sm font-semibold text-ink">
        <Switch checked={active} disabled={disabled} aria-label={name} onChange={() => setAsking(true)} />
        <span className={active ? 'text-ink' : 'text-ink-subtle'}>{active ? copy.active : copy.inactive}</span>
      </label>
      <ConfirmDialog
        open={asking}
        onOpenChange={setAsking}
        tone={active ? 'danger' : 'action'}
        title={active ? copy.deactivateTitle : copy.activateTitle}
        body={active ? copy.deactivateBody : copy.activateBody}
        confirmLabel={active ? copy.deactivate : copy.activate}
        cancelLabel={copy.cancel}
        closeLabel={copy.close}
        onConfirm={() => onConfirm(active ? 'INACTIVE' : 'ACTIVE')}
      />
    </>
  );
}
